import React from "react";
import {autobind} from "core-decorators";
import {Button, Space, Tooltip, Typography} from "antd";
import {InfoCircleOutlined, PlusOutlined, ReadOutlined} from "@ant-design/icons";
import {RiBook3Line} from "react-icons/ri";
import {action, computed, observable} from "mobx";
import {disposeOnUnmount, inject, observer} from "mobx-react";
import {Link, RouteComponentProps} from "react-router-dom";
import _sortBy from "lodash/sortBy";
import _isEmpty from "lodash/isEmpty";

import "./Pieces.scss";
import {PendingPieceStatuses} from "@/shared/constants";
import {PiecesQuery} from "@/graphql/types";
import {paths} from "@/routes";
import AddPieceUpgradeModal from "@/components/AddPieceUpgradeModal/AddPieceUpgradeModal";
import getPieces from "@/graphql/queries/getPieces";
import PageLayout from "@/components/PageLayout/PageLayout";
import Store from "@/stores";
import TablePendingPieces from "./TablePendingPieces";
import TablePieces from "./TablePieces";

export interface IPiecesProps extends RouteComponentProps {
  store?: Store;
}

@inject("store")
@autobind
@observer
export default class Pieces extends React.Component<IPiecesProps> {
  @observable pieces?: PiecesQuery["pieces"];
  @observable addPieceModalVisible = false;

  async componentDidMount() {
    await this.fetchPieces();
    // Keep refreshing pieces
    const intervalId = setInterval(this.fetchPieces, 2000);
    disposeOnUnmount(this, () => clearInterval(intervalId));
  }

  closeAddPieceModal() {
    this.addPieceModalVisible = false;
  }

  async fetchPieces() {
    const store = this.props.store!;
    if (!store.session.selectedEnsemble) throw new Error("No ensemble selected");
    // Do not fetch if we have signed out
    if (!store.session.isSignedIn) return;
    const pieces = await getPieces(store.apollo.client, store.session.selectedEnsemble.id);
    if (!store.session.isSignedIn) return; // stop if we have signed out
    this.setPieces(pieces);
  }

  openAddPieceModal() {
    this.addPieceModalVisible = true;
  }

  @computed({keepAlive: true})
  get pendingPieces(): PiecesQuery["pieces"] | undefined {
    let pieces = this.pieces?.filter((piece) => PendingPieceStatuses.find((status) => status === piece.status));
    pieces = _sortBy(pieces, ["createdAt"]).reverse();
    return pieces;
  }

  @action
  setPieces(pieces?: PiecesQuery["pieces"]) {
    this.pieces = pieces;
  }

  @computed({keepAlive: true})
  get showPendingPieces(): boolean {
    return this.props.store!.session.isEnsembleConductor && !_isEmpty(this.pendingPieces);
  }

  public render() {
    const {session} = this.props.store!;
    return (
      <PageLayout>
        <div className="Pieces">
          <div className="title-and-buttons">
            <Typography.Title level={3} className="title">
              <ReadOutlined className="title-icon" />
              My Repertoire
            </Typography.Title>
            {session.isEnsembleConductor && (
              <Space className="top-buttons">
                <Link to={paths.catalog()}>
                  <Button
                    type="primary"
                    icon={
                      <span role="img" aria-label="catalog" className="anticon">
                        <RiBook3Line />
                      </span>
                    }
                    size="small"
                  >
                    Add from Catalog
                  </Button>
                </Link>
                {session.canAddPiece ? (
                  <Link to={paths.pieceNew()}>
                    <Button type="primary" icon={<PlusOutlined />} size="small">
                      Add Piece
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Button type="primary" icon={<PlusOutlined />} size="small" onClick={this.openAddPieceModal}>
                      Add Piece
                    </Button>
                    <AddPieceUpgradeModal close={this.closeAddPieceModal} visible={this.addPieceModalVisible} />
                  </>
                )}
              </Space>
            )}
          </div>
          {this.showPendingPieces && (
            <>
              <Typography.Title level={4} className="title" style={{marginBottom: 0}}>
                Pending Pieces
                <small>
                  &nbsp;
                  <Tooltip
                    title="This table contains pieces that are being synthesized."
                    placement="right"
                    arrowPointAtCenter
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </small>
              </Typography.Title>
              <TablePendingPieces pieces={this.pieces} pendingPieces={this.pendingPieces} />
            </>
          )}
          {
            // do not show the title if the pending pieces table is not shown
            this.showPendingPieces && (
              <Typography.Title level={4} className="title" style={{marginBottom: 0}}>
                Pieces
              </Typography.Title>
            )
          }
          <TablePieces pieces={this.pieces} />
        </div>
      </PageLayout>
    );
  }
}
